import { useContext, useMemo } from 'react'

import { isArray, isFunction } from 'lodash'

import { GlobalContext } from '../../../../Providers/GlobalProvider'
import EntitiesSelector from '../EntitiesSelector'
import { isObject } from '../../../../utils'
import { InstitutionModalContext } from '../../../../Providers/Forms/InstitutionModal/InstitutionModal'
import { InstitutionGroupContext } from '../../../../Providers/InstitutionGroupProvider'
import CategorizedCascader from '../CategorizedCascader'
import { getEntityRank } from '../../Badges/EntityBadge'
import { sortOptionsTree } from '../utils/options'

/**
 * @param {boolean} grouped entities are displayed as a cascader grouped by category, in which case
 * `institutions` is expected to hold the category tree built by buildCategoryTree
 */
const InstitutionsSelector = ({
  className = '', disabled, dropdownRender, empty = false, grouped = false, institutions, multiple = 'default', placeholder = '', reverse = false, style = {}, values,
  renderOption, onChange, applyCustomStyles = false, onSearch, loading
}) => {
  const gc = useContext(GlobalContext)
  const modalContext = useContext(InstitutionModalContext)
  const ig = useContext(InstitutionGroupContext)

  const cascaderOptions = useMemo(() => grouped ? sortOptionsTree(institutions) : [], [grouped, institutions])

  const institutionEntities = useMemo(() => {
    let institutionEntities = []

    if (isArray(institutions)) {
      institutionEntities = institutions
    } else if (isObject(ig)) {
      institutionEntities = ig.institutions
    } else if (isObject(gc)) {
      institutionEntities = gc.institutions
    }

    return institutionEntities
      .filter(i => isObject(i) && i.id !== null && i.id !== undefined)
      .sort((a, b) => {
        const rankDiff = getEntityRank(a) - getEntityRank(b)

        if (rankDiff !== 0) {
          return rankDiff
        }

        return (a.acronym ?? a.name ?? '').localeCompare(b.acronym ?? b.name ?? '')
      })
  }, [institutions, gc, ig])

  const institutionsOptions = useMemo(() => institutionEntities.map(institution => {
    const values = {
      label: institution.acronym ?? institution.name,
      value: institution.id,
      tooltip: institution.name,
      entity: institution
    }

    if (reverse) {
      values.stored = values.label
      values.label = values.tooltip
      values.tooltip = values.stored
      delete values.stored
    }

    return values
  }), [institutionEntities])

  return (
    <div className={'flex-col w-100 ' + className}>
      {grouped ? (
        <CategorizedCascader
          options={cascaderOptions}
          value={values}
          disabled={disabled}
          style={style}
          placeholder={placeholder}
          applyCustomStyles={applyCustomStyles}
          onChange={onChange}
        />
      ) : (
        <EntitiesSelector
          selectorClassName='entities-selector-custom'
          disabled={disabled}
          empty={empty}
          entityOptions={institutionsOptions}
          multiple={multiple}
          placeholder={placeholder}
          showSearch
          style={style}
          values={values}
          dropdownRender={
            modalContext !== undefined && isFunction(dropdownRender)
              ? dropdownRender(modalContext.setFormHandler)
              : undefined
          }
          onChange={onChange}
          onSearch={onSearch}
          renderOption={renderOption}
        />
      )}
    </div>
  )
}

export default InstitutionsSelector
